import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { PortalAuthLayout } from '@/components/portal/PortalAuthLayout';
import { usePortalRegister } from '@/hooks/portal/usePortalAuth';

export const Route = createFileRoute('/(portal)/portal/register')({
  component: PortalRegisterPage,
});

const schema = z.object({ email: z.string().email('Enter a valid email address') });
type Values = z.infer<typeof schema>;

function PortalRegisterPage() {
  const register = usePortalRegister();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  async function onSubmit(values: Values) {
    // Response is intentionally identical whether or not the email is on file.
    await register.mutateAsync(values.email).catch(() => undefined);
  }

  if (register.isSuccess) {
    return (
      <PortalAuthLayout
        title="Check your email"
        description="If that address is on file, we just sent a link to finish setting up your account."
        footer={
          <Link to="/portal/login" className="font-medium text-foreground underline">
            Back to sign in
          </Link>
        }
      >
        <p className="text-sm text-muted-foreground">
          The link expires in 24 hours. If your email is set up with more than one company,
          you&apos;ll get a link for each. Didn&apos;t get it? Check your spam folder, then try
          again.
        </p>
      </PortalAuthLayout>
    );
  }

  return (
    <PortalAuthLayout
      title="Request access"
      description="Enter the email your contractor has on file to set up your portal."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/portal/login" className="font-medium text-foreground underline">
            Sign in
          </Link>
        </>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full" disabled={register.isPending}>
            {register.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Send setup link
          </Button>
        </form>
      </Form>
    </PortalAuthLayout>
  );
}
